// Sezione "Account collegati" della scheda cliente: elenca gli alias email (esiti
// di merge precedenti) e permette di collegarne altri. Il merge fonde un secondo
// cliente NEL corrente: la sua email diventa alias, i suoi ordini confluiscono.
// Solo chiavi email — il parent monta questa sezione solo se !isFallbackKey.
import { useEffect, useState } from "react";
import { Card } from "@/shared/ui/Card";
import { prettifyApiError } from "@/shared/api";
import { fetchCustomers, mergeCustomers } from "../api";
import type { CustomerListItem } from "../types";

interface LinkedAccountsSectionProps {
  /** Chiave del cliente corrente ('e:...'): sopravvive al merge (è il canonico). */
  customerKey: string;
  aliases: string[];
  /** Refetch del dettaglio dopo il merge (ordini/KPI/alias cambiano). */
  onMerged: () => void;
}

export function LinkedAccountsSection({
  customerKey,
  aliases,
  onMerged,
}: LinkedAccountsSectionProps) {
  const [showDialog, setShowDialog] = useState(false);

  return (
    <Card title="Account collegati" className="mb-4">
      {aliases.length === 0 ? (
        <p className="text-[12px] text-ink-2">Nessun account collegato.</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {aliases.map((email) => (
            <li key={email} className="tabular-nums text-[12px] text-ink-2">
              {email}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3">
        <button
          type="button"
          onClick={() => setShowDialog(true)}
          className="rounded-sm border border-line bg-card px-3 py-1.5 text-[12px] font-medium text-ink-2 transition-colors hover:border-accent hover:bg-paper"
        >
          Collega account…
        </button>
      </div>

      {showDialog && (
        <MergeDialog
          customerKey={customerKey}
          onClose={() => setShowDialog(false)}
          onMerged={onMerged}
        />
      )}
    </Card>
  );
}

function MergeDialog({
  customerKey,
  onClose,
  onMerged,
}: {
  customerKey: string;
  onClose: () => void;
  onMerged: () => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<CustomerListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CustomerListItem | null>(null);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESC chiude (ignorato durante il merge).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !merging) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [merging, onClose]);

  // Ricerca debounced (300ms). Min 2 char per non spammare; esclude il cliente
  // corrente dai risultati (un self-merge sarebbe comunque 422 dal backend).
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }
    const ctrl = new AbortController();
    setSearching(true);
    setSearchError(null);
    const timer = window.setTimeout(() => {
      fetchCustomers({ q: term, limit: 8, offset: 0 }, ctrl.signal)
        .then((rows) => setResults(rows.filter((r) => r.key !== customerKey)))
        .catch((err) => {
          // Un errore reale (500/backend giù) non deve apparire come «Nessun
          // cliente trovato»: distinguilo con searchError + prettifyApiError.
          if (!ctrl.signal.aborted) {
            setSearchError(prettifyApiError(err));
            setResults([]);
          }
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setSearching(false);
        });
    }, 300);
    return () => {
      window.clearTimeout(timer);
      ctrl.abort();
    };
  }, [q, customerKey]);

  async function confirmMerge() {
    if (!selected || merging) return;
    setMerging(true);
    setError(null);
    try {
      await mergeCustomers(customerKey, selected.key);
      onMerged();
      onClose();
    } catch (err) {
      setError(prettifyApiError(err));
      setMerging(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Collega account cliente"
      onClick={(e) => {
        if (e.target === e.currentTarget && !merging) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-line bg-card p-5 shadow-popover">
        <h3 className="font-display text-[16px] font-bold text-ink">Collega account</h3>

        {selected ? (
          <div className="mt-3">
            <div className="rounded-md border border-line bg-paper px-3 py-2">
              <div className="text-[13px] font-semibold text-ink">{selected.name}</div>
              <div className="tabular-nums text-[11.5px] text-ink-2">{selected.email}</div>
            </div>
            <p className="mt-3 rounded-md border border-amber/30 bg-amber-soft px-3 py-2 text-[12px] text-ink">
              Le due schede diventano una: gli ordini di questo account confluiscono nel cliente
              corrente e la sua email diventa un alias. L'operazione non è automaticamente
              reversibile.
            </p>
            {error && (
              <p role="alert" className="mt-2 text-[12px] text-red">
                {error}
              </p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSelected(null)}
                disabled={merging}
                className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink-2 hover:border-ink-4 disabled:opacity-50"
              >
                Indietro
              </button>
              <button
                type="button"
                onClick={() => void confirmMerge()}
                disabled={merging}
                className="rounded-md border border-teal bg-teal px-3 py-2 text-[12px] font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {merging ? "Unisco…" : "Conferma merge"}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3">
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              placeholder="Cerca cliente da collegare…"
              aria-label="Cerca cliente da collegare"
              className="w-full rounded-md border border-line bg-card px-3 py-2 text-[13px] text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 placeholder:text-ink-4"
            />
            <div className="mt-2 max-h-[260px] overflow-y-auto">
              {searching ? (
                <p className="px-1 py-2 text-[12px] text-ink-2">Cerco…</p>
              ) : q.trim().length < 2 ? (
                <p className="px-1 py-2 text-[12px] text-ink-2">
                  Digita almeno 2 caratteri (nome, email o città).
                </p>
              ) : searchError ? (
                <p role="alert" className="px-1 py-2 text-[12px] text-red">
                  {searchError}
                </p>
              ) : results.length === 0 ? (
                <p className="px-1 py-2 text-[12px] text-ink-2">Nessun cliente trovato.</p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {results.map((c) => (
                    <li key={c.key}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelected(c);
                          setError(null);
                        }}
                        className="flex w-full flex-col items-start rounded-md border border-line bg-card px-3 py-2 text-left transition-colors hover:border-accent hover:bg-paper"
                      >
                        <span className="text-[12.5px] font-semibold text-ink">{c.name}</span>
                        <span className="tabular-nums text-[11px] text-ink-2">
                          {[c.email, c.city].filter(Boolean).join(" · ")}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink-2 hover:border-ink-4"
              >
                Annulla
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
